import { useCallback, useEffect, useRef, useState } from "react";

import {
    getAttendanceDeviceData,
    getAttendanceQrFrameBounds
} from "components/attendance/helpers";
import useAttendanceMutationScan from "./attendance-use-mutation-scan";


export const ATTENDANCE_SCAN_STATES = {
    IDLE: "idle",
    SCANNING: "scanning",
    LOCATING: "locating",
    SUBMITTING: "submitting",
    RESULT: "result",
    FAILED: "failed"
};

export const ATTENDANCE_ACTIONS = {
    CHECK_IN: "check_in",
    CHECK_OUT: "check_out"
};

const GEOLOCATION_OPTIONS = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0
};

const QR_CAPTURE_FEEDBACK_DELAY = 320;

const QR_READER_OPTIONS = {
    delayBetweenScanAttempts: 100,
    delayBetweenScanSuccess: 100,
    tryPlayVideoTimeout: 10000
};

const CAMERA_START_CONSTRAINTS = [
    {
        facingMode: { exact: "environment" },
        width: { ideal: 1280 },
        height: { ideal: 720 }
    },
    {
        facingMode: { ideal: "environment" },
        width: { ideal: 1280 },
        height: { ideal: 720 }
    },
    true
];

const EXPECTED_DECODE_ERROR_NAMES = new Set([
    "ChecksumException",
    "FormatException",
    "NotFoundException"
]);

const getReaderId = () => `attendance-reader-${ Date.now() }-${ Math.random().toString(16).slice(2) }`;

export const getAttendanceScannerCameraId = (readerId) => `${ readerId }-camera`;

const waitForReaderElement = (readerId, timeout = 2000) => new Promise((resolve) => {
    const startedAt = Date.now();

    const check = () => {
        const readerElement = document.getElementById(readerId);

        if (readerElement) {
            resolve(readerElement);

            return;
        }

        if (Date.now() - startedAt >= timeout) {
            resolve(null);

            return;
        }

        window.requestAnimationFrame(check);
    };

    check();
});

const getSecureContextFailure = (kind = "camera") => ({
    kind,
    reason: "insecure_context",
    title: "ATTENDANCE.Error_SecureContext_Title",
    text: "ATTENDANCE.Error_SecureContext_Text"
});

const isSecureBrowserContext = () => (
    typeof window === "undefined" || window.isSecureContext !== false
);

const loadBrowserQrCodeReader = async () => {
    const module = await import("@zxing/browser");

    return module.BrowserQRCodeReader;
};

const RESULT_BY_STATUS = {
    success: "accepted",
    warning: "ignored",
    rejected: "rejected"
};

const normalizeReason = (reason) => String(reason || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const normalizeResult = (payload) => {
    const workSessionLog = payload?.workSessionLog || {};
    const status = String(workSessionLog.status || "").toLowerCase();

    return {
        result: RESULT_BY_STATUS[status] || "rejected",
        status,
        reason: normalizeReason(workSessionLog.reason),
        detail: workSessionLog.detail || payload?.message || "",
        label: payload?.label || "",
        message: payload?.message || "",
        server_time: workSessionLog.created_at,
        attendance_shift_id: workSessionLog.work_session_id,
        action: workSessionLog.action
    };
};

const getErrorMessage = (error) => {
    if (!error) {
        return "";
    }

    if (typeof error === "string") {
        return error;
    }

    return error.message || error.name || String(error);
};

const isCameraPermissionError = (error) => (
    /notallowed|permission|denied|securityerror/i.test(getErrorMessage(error))
);

const isCameraNotFoundError = (error) => (
    /notfound|overconstrained|constraint|no camera|requested device not found/i.test(getErrorMessage(error))
);

const getCameraFailure = (error) => {
    if (!window.navigator?.mediaDevices?.getUserMedia) {
        return {
            kind: "camera",
            reason: "camera_unsupported",
            title: "ATTENDANCE.Error_Camera_Title",
            text: "ATTENDANCE.Error_Camera_Text"
        };
    }

    if (isCameraPermissionError(error)) {
        return {
            kind: "camera",
            reason: "camera_permission_denied",
            title: "ATTENDANCE.Error_CameraPermission_Title",
            text: "ATTENDANCE.Error_CameraPermission_Text"
        };
    }

    if (isCameraNotFoundError(error)) {
        return {
            kind: "camera",
            reason: "camera_not_found",
            title: "ATTENDANCE.Error_CameraNotFound_Title",
            text: "ATTENDANCE.Error_CameraNotFound_Text"
        };
    }

    return {
        kind: "camera",
        reason: "camera_unavailable",
        title: "ATTENDANCE.Error_Camera_Title",
        text: "ATTENDANCE.Error_Camera_Text"
    };
};

const startScannerWithConstraints = async (reader, videoElement, onDecode) => {
    let lastError = null;

    for (const videoConstraints of CAMERA_START_CONSTRAINTS) {
        try {
            return await reader.decodeFromConstraints(
                {
                    audio: false,
                    video: videoConstraints
                },
                videoElement,
                onDecode
            );
        } catch (e) {
            lastError = e;

            if (isCameraPermissionError(e)) {
                throw e;
            }
        }
    }

    throw lastError || new Error("Camera unavailable");
};

const disposeScanner = async (scanner) => {
    if (!scanner) {
        return;
    }

    try {
        await Promise.resolve(scanner.controls?.stop?.());
    } catch (e) {
        // A scanner can be disposed before start has completed.
    }

    const videoElement = scanner.videoElement;
    const stream = videoElement?.srcObject;

    if (stream?.getTracks) {
        stream.getTracks().forEach((track) => track.stop());
    }

    if (videoElement) {
        videoElement.srcObject = null;
    }

    scanner.controls = null;
};

const isExpectedDecodeError = (error) => (
    EXPECTED_DECODE_ERROR_NAMES.has(error?.name || error?.constructor?.name)
);

const getGeolocationFailure = (error) => {
    if (error?.code === 1) {
        return {
            kind: "location",
            reason: "permission_denied",
            title: "ATTENDANCE.Error_GpsPermission_Title",
            text: "ATTENDANCE.Error_GpsPermission_Text"
        };
    }

    if (error?.code === 3) {
        return {
            kind: "location",
            reason: "gps_timeout",
            title: "ATTENDANCE.Error_GpsTimeout_Title",
            text: "ATTENDANCE.Error_GpsTimeout_Text"
        };
    }

    return {
        kind: "location",
        reason: "gps_unavailable",
        title: "ATTENDANCE.Error_GpsUnavailable_Title",
        text: "ATTENDANCE.Error_GpsUnavailable_Text"
    };
};

const useAttendanceScan = ({ action, eeId }) => {
    const scannerRef = useRef(null);
    const scannerStartRef = useRef(null);
    const scannerCleanupRef = useRef(Promise.resolve());
    const scanLockedRef = useRef(false);
    const qrPayloadRef = useRef(null);
    const operationIdRef = useRef(0);
    const mountedRef = useRef(false);
    const captureTimeoutRef = useRef(null);
    const decodeErrorLoggedRef = useRef(false);

    const [ readerId, setReaderId ] = useState(getReaderId);
    const [ scanState, setScanState ] = useState(ATTENDANCE_SCAN_STATES.IDLE);
    const [ result, setResult ] = useState(null);
    const [ failure, setFailure ] = useState(null);
    const [ detectedQrBounds, setDetectedQrBounds ] = useState(null);

    const { attendanceScan } = useAttendanceMutationScan();

    const reportPermissionDenied = useCallback(({
        qrPayload = null,
        cameraPermissionDenied = false,
        locationPermissionDenied = false
    }) => {
        attendanceScan({
            ee_id: eeId,
            action,
            qr_payload: qrPayload,
            camera_permission_denied: cameraPermissionDenied,
            location_permission_denied: locationPermissionDenied,
            ...getAttendanceDeviceData()
        }).catch(() => undefined);
    }, [
        action,
        attendanceScan,
        eeId
    ]);

    const setStateIfMounted = useCallback((callback) => {
        if (mountedRef.current) {
            callback();
        }
    }, []);

    const isActiveOperation = useCallback((operationId) => (
        mountedRef.current && operationIdRef.current === operationId
    ), []);

    const clearCaptureTimeout = useCallback(() => {
        if (captureTimeoutRef.current !== null) {
            window.clearTimeout(captureTimeoutRef.current);
            captureTimeoutRef.current = null;
        }
    }, []);

    const queueScannerDisposal = useCallback((scanner, startPromise = null) => {
        const cleanupPromise = scannerCleanupRef.current
            .catch(() => undefined)
            .then(async () => {
                if (startPromise) {
                    try {
                        await startPromise;
                    } catch (e) {
                        // A failed start still needs its reader element cleared.
                    }
                }

                await disposeScanner(scanner);
            });

        scannerCleanupRef.current = cleanupPromise;

        return cleanupPromise;
    }, []);

    const stopScanner = useCallback(() => {
        clearCaptureTimeout();

        const startRecord = scannerStartRef.current;
        const scanner = scannerRef.current || startRecord?.scanner;

        if (!scanner) {
            return scannerCleanupRef.current;
        }

        scannerRef.current = null;

        if (startRecord?.scanner === scanner) {
            scannerStartRef.current = null;
        }

        return queueScannerDisposal(scanner, startRecord?.promise);
    }, [
        clearCaptureTimeout,
        queueScannerDisposal
    ]);

    const submitScan = useCallback(async (qrPayload, position, operationId) => {
        if (!isActiveOperation(operationId)) {
            return;
        }

        const { latitude, longitude, accuracy } = position.coords || {};

        setStateIfMounted(() => {
            setScanState(ATTENDANCE_SCAN_STATES.SUBMITTING);
        });

        try {
            const { data } = await attendanceScan({
                ee_id: eeId,
                action,
                qr_payload: qrPayload,
                camera_permission_denied: false,
                location_permission_denied: false,
                latitude,
                longitude,
                accuracy,
                ...getAttendanceDeviceData()
            });

            const scanPayload = data?.workSessionLogScan;

            if (!scanPayload) {
                throw new Error("Empty work session scan payload");
            }

            if (!isActiveOperation(operationId)) {
                return;
            }

            setStateIfMounted(() => {
                setResult(normalizeResult(scanPayload));
                setScanState(ATTENDANCE_SCAN_STATES.RESULT);
            });
        } catch (e) {
            if (!isActiveOperation(operationId)) {
                return;
            }

            setStateIfMounted(() => {
                setFailure({
                    kind: "server",
                    reason: "server_error",
                    title: "ATTENDANCE.Error_Server_Title",
                    text: "ATTENDANCE.Error_Server_Text",
                    detail: e?.message || ""
                });
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            });
        }
    }, [
        action,
        attendanceScan,
        eeId,
        isActiveOperation,
        setStateIfMounted
    ]);

    const requestLocation = useCallback((qrPayload, operationId = operationIdRef.current) => {
        if (!qrPayload || !isActiveOperation(operationId)) {
            return;
        }

        qrPayloadRef.current = qrPayload;

        setStateIfMounted(() => {
            setFailure(null);
            setScanState(ATTENDANCE_SCAN_STATES.LOCATING);
        });

        if (!isSecureBrowserContext()) {
            setStateIfMounted(() => {
                setFailure(getSecureContextFailure("location"));
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            });

            return;
        }

        if (!window.navigator?.geolocation) {
            setStateIfMounted(() => {
                setFailure({
                    kind: "location",
                    reason: "gps_unsupported",
                    title: "ATTENDANCE.Error_GpsUnavailable_Title",
                    text: "ATTENDANCE.Error_GpsUnavailable_Text"
                });
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            });

            return;
        }

        window.navigator.geolocation.getCurrentPosition(
            (position) => {
                if (isActiveOperation(operationId)) {
                    submitScan(qrPayload, position, operationId);
                }
            },
            (error) => {
                if (!isActiveOperation(operationId)) {
                    return;
                }

                const locationFailure = getGeolocationFailure(error);

                if (locationFailure.reason === "permission_denied") {
                    reportPermissionDenied({
                        qrPayload,
                        locationPermissionDenied: true
                    });
                }

                setStateIfMounted(() => {
                    setFailure(locationFailure);
                    setScanState(ATTENDANCE_SCAN_STATES.FAILED);
                });
            },
            GEOLOCATION_OPTIONS
        );
    }, [
        isActiveOperation,
        reportPermissionDenied,
        setStateIfMounted,
        submitScan
    ]);

    const handleScanSuccess = useCallback((scanResult, activeReaderId) => {
        const decodedText = scanResult?.getText?.();

        if (!decodedText || scanLockedRef.current) {
            return;
        }

        scanLockedRef.current = true;
        qrPayloadRef.current = decodedText;
        const operationId = operationIdRef.current;
        const videoElement = document.getElementById(activeReaderId);
        const cameraElement = document.getElementById(
            getAttendanceScannerCameraId(activeReaderId)
        );
        const qrBounds = getAttendanceQrFrameBounds(
            scanResult,
            videoElement,
            cameraElement
        );

        setStateIfMounted(() => {
            setDetectedQrBounds(qrBounds);
        });

        captureTimeoutRef.current = window.setTimeout(() => {
            captureTimeoutRef.current = null;

            if (!isActiveOperation(operationId)) {
                return;
            }

            stopScanner().finally(() => {
                requestLocation(decodedText, operationId);
            });
        }, qrBounds ? QR_CAPTURE_FEEDBACK_DELAY : 0);
    }, [
        isActiveOperation,
        requestLocation,
        setStateIfMounted,
        stopScanner
    ]);

    const startScanner = useCallback(async () => {
        const operationId = operationIdRef.current + 1;

        operationIdRef.current = operationId;
        scanLockedRef.current = false;
        qrPayloadRef.current = null;
        decodeErrorLoggedRef.current = false;

        setResult(null);
        setFailure(null);
        setDetectedQrBounds(null);

        await stopScanner();

        if (!isActiveOperation(operationId)) {
            return;
        }

        if (!isSecureBrowserContext()) {
            setFailure(getSecureContextFailure());
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);

            return;
        }

        if (!window.navigator?.mediaDevices?.getUserMedia) {
            setFailure(getCameraFailure());
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);

            return;
        }

        const nextReaderId = getReaderId();

        setReaderId(nextReaderId);

        const readerElement = await waitForReaderElement(nextReaderId);

        if (!isActiveOperation(operationId)) {
            return;
        }

        if (!readerElement) {
            setFailure(getCameraFailure());
            setScanState(ATTENDANCE_SCAN_STATES.FAILED);

            return;
        }

        let BrowserQRCodeReader;

        try {
            BrowserQRCodeReader = await loadBrowserQrCodeReader();
        } catch (e) {
            if (isActiveOperation(operationId)) {
                setFailure(getCameraFailure(e));
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            }

            return;
        }

        const videoElement = document.getElementById(nextReaderId);

        if (!isActiveOperation(operationId) || !videoElement) {
            return;
        }

        const scanner = {
            reader: new BrowserQRCodeReader(undefined, QR_READER_OPTIONS),
            controls: null,
            videoElement
        };

        scannerRef.current = scanner;
        setScanState(ATTENDANCE_SCAN_STATES.SCANNING);

        const onDecode = (scanResult, scanError) => {
            if (scanResult) {
                handleScanSuccess(scanResult, nextReaderId);

                return;
            }

            if (
                scanError &&
                !isExpectedDecodeError(scanError) &&
                !decodeErrorLoggedRef.current
            ) {
                decodeErrorLoggedRef.current = true;
                console.warn("Attendance QR decoder error:", scanError);
            }
        };

        const startPromise = (async () => {
            const controls = await startScannerWithConstraints(
                scanner.reader,
                videoElement,
                onDecode
            );

            scanner.controls = controls;

            return (
                isActiveOperation(operationId) &&
                scannerRef.current === scanner
            );
        })();

        scannerStartRef.current = {
            scanner,
            promise: startPromise
        };

        try {
            const scannerStarted = await startPromise;

            if (scannerStartRef.current?.scanner === scanner) {
                scannerStartRef.current = null;
            }

            if (!scannerStarted) {
                if (scannerRef.current === scanner) {
                    scannerRef.current = null;
                    await queueScannerDisposal(scanner);
                } else {
                    await scannerCleanupRef.current;
                }

                return;
            }

            if (
                !isActiveOperation(operationId) ||
                scannerRef.current !== scanner
            ) {
                if (scannerRef.current === scanner) {
                    scannerRef.current = null;
                }

                await queueScannerDisposal(scanner);

                return;
            }

        } catch (e) {
            if (scannerStartRef.current?.scanner === scanner) {
                scannerStartRef.current = null;
            }

            if (scannerRef.current === scanner) {
                scannerRef.current = null;
            }

            await queueScannerDisposal(scanner);

            if (isActiveOperation(operationId)) {
                const cameraFailure = getCameraFailure(e);

                if (cameraFailure.reason === "camera_permission_denied") {
                    reportPermissionDenied({
                        cameraPermissionDenied: true
                    });
                }

                setFailure(cameraFailure);
                setScanState(ATTENDANCE_SCAN_STATES.FAILED);
            }
        }
    }, [
        handleScanSuccess,
        isActiveOperation,
        queueScannerDisposal,
        reportPermissionDenied,
        stopScanner
    ]);

    const retry = useCallback(() => {
        if (failure?.kind === "location" && qrPayloadRef.current) {
            operationIdRef.current += 1;
            requestLocation(qrPayloadRef.current, operationIdRef.current);

            return;
        }

        startScanner();
    }, [
        failure,
        requestLocation,
        startScanner
    ]);

    const reset = useCallback(() => {
        operationIdRef.current += 1;
        clearCaptureTimeout();
        scanLockedRef.current = false;
        qrPayloadRef.current = null;
        decodeErrorLoggedRef.current = false;
        setResult(null);
        setFailure(null);
        setDetectedQrBounds(null);
        setScanState(ATTENDANCE_SCAN_STATES.IDLE);
    }, [ clearCaptureTimeout ]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            operationIdRef.current += 1;
            stopScanner();
        };
    }, [ stopScanner ]);

    return {
        readerId,
        cameraId: getAttendanceScannerCameraId(readerId),
        scanState,
        result,
        failure,
        detectedQrBounds,
        startScanner,
        stopScanner,
        retry,
        reset
    };
};

export default useAttendanceScan;
